import { Divider, Group, SegmentedControl, Text, Tooltip } from '@byecode/ui'
import type { Field, IconDecorationProtocol } from '@lighthouse/core'
import { AudioVariant, BACKGROUND_TYPE } from '@lighthouse/core'
import {
    audioFileSizeFilter,
    defaultFileMaxUploadSize,
    getImageFullUrlInApplication,
    getIsFindUseInVariable,
    IconPicker,
    ListItem4ByecodeUi,
    useAtomData,
    useFindUseObjectContext,
    useSpaceQuotaContext,
    VariableSelect
} from '@lighthouse/shared'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { lastPageOfStackAtom, pageAtomFamily, pageVariableSchemasFamily } from '@/atoms/page/state'
import { AnchorSetting } from '@/components/AnchorSetting'
import { EventList } from '@/components/EventList'
import { getBlockAndPageDesignLimit } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { FillColor } from '@/components/StyleSetting/components/FillColor'
import { PositionSetting } from '@/components/StyleSetting/PositionSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { AUDIO_VARIANT_DEFAULT_SIZE } from '@/constants/Block/generate/audio'
import { useCurrentAppId } from '@/hooks/useApplication'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'
import { uploadManagerInAppParams } from '@/utils/auth'

import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { SwitchControl } from '../Common/SwitchControl'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../constants'

const AUDIO_FIELD: Field = { type: 'file', id: 'audio', dsId: '', name: '音频', innerType: 'ARRAY' }
const EMPTY_AUDIO_ICON: IconDecorationProtocol = { type: 'icon', value: '' }

const audioIconPickerValue = (specific?: IconDecorationProtocol, legacy?: IconDecorationProtocol) => {
    if (specific?.value) {
        return specific
    }
    if (specific) {
        return undefined
    }
    return legacy?.value ? legacy : undefined
}

const AUDIO_VARIANT_OPTIONS = [
    { label: '语音', value: AudioVariant.voice },
    { label: '播放条', value: AudioVariant.bar },
    { label: '仅按钮', value: AudioVariant.button }
]

export const AudioBlockSetting: React.FC = () => {
    const styleProps = getBlockAndPageDesignLimit('audio')
    const disabledWithVersion = useIsDisabledWithVersion()
    const { control, setValue, watch } = useFormContext()
    const appId = useCurrentAppId()
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const findUseObject = useFindUseObjectContext()
    const { quota } = useSpaceQuotaContext()
    const pageName = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => s?.name ?? '', [])
    )
    const pageVariableSchemas = useAtomData(pageVariableSchemasFamily(pageId))

    const variant = watch('variant') as AudioVariant | undefined
    const legacyIcon = watch('icon') as IconDecorationProtocol | undefined
    const fileMaxUploadSize = quota?.uploadFileLimitSize ?? defaultFileMaxUploadSize
    const audioUploadProps = useMemo(
        () => ({
            multiple: false,
            accept: 'audio/*',
            uploadOptions: {
                info: { id: '', label: pageName, groupId: pageId },
                options: {
                    ...uploadManagerInAppParams(),
                    fileFilter: audioFileSizeFilter(fileMaxUploadSize)
                }
            }
        }),
        [fileMaxUploadSize, pageId, pageName]
    )

    const { customViewOption } = useVariableCustomViewOption()
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()

    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE)
    const showDurationSetting = variant !== AudioVariant.button

    return (
        <>
            <BlockConfigureSegmentedControl value={currentTab} onChange={setCurrentTab} />

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <PositionSetting {...styleProps.position} />
                    <Divider />
                    <SizeSetting {...styleProps.size} />
                    <Divider />
                    <VisibilitySetting />
                    <Divider />
                    <AnchorSetting />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    <Group label="基础">
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>音频来源</Text>
                            <Controller
                                name="audioSource"
                                control={control}
                                render={({ field }) => {
                                    const isHighLight = getIsFindUseInVariable({
                                        variable: field.value,
                                        findUseObject,
                                        pageVariableSchemas
                                    })
                                    return (
                                        <VariableSelect
                                            innerTypes={['ARRAY', 'TEXT']}
                                            field={AUDIO_FIELD}
                                            viewOptions={customViewOption ? [customViewOption] : undefined}
                                            pageOptions={pageOptions}
                                            userOption={userOption}
                                            highlighting={isHighLight}
                                            withinPortal
                                            disablePexels
                                            disabled={disabledWithVersion}
                                            placeholder="粘贴音频链接"
                                            uploadProps={audioUploadProps}
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    )
                                }}
                            />
                        </ListItem4ByecodeUi>
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>组件类型</Text>
                            <div style={{ width: 180 }}>
                                <Controller
                                    name="variant"
                                    control={control}
                                    render={({ field }) => (
                                        <SegmentedControl
                                            data={AUDIO_VARIANT_OPTIONS}
                                            fullWidth
                                            value={field.value ?? AudioVariant.bar}
                                            onChange={v => {
                                                const next = v as AudioVariant
                                                field.onChange(next)
                                                setValue('breakPoint.size', AUDIO_VARIANT_DEFAULT_SIZE[next])
                                            }}
                                        />
                                    )}
                                />
                            </div>
                        </ListItem4ByecodeUi>
                        {showDurationSetting && (
                            <Controller
                                name="showDuration"
                                control={control}
                                render={({ field }) => (
                                    <SwitchControl
                                        label="显示音频时长"
                                        checked={field.value ?? true}
                                        onChange={e => field.onChange?.(e.currentTarget.checked)}
                                    />
                                )}
                            />
                        )}
                        <Controller
                            name="isLoop"
                            control={control}
                            render={({ field }) => (
                                <SwitchControl
                                    label="循环播放"
                                    checked={field.value ?? false}
                                    onChange={e => field.onChange?.(e.currentTarget.checked)}
                                />
                            )}
                        />
                        <Controller
                            name="preload"
                            control={control}
                            render={({ field }) => (
                                <SwitchControl
                                    label="预加载"
                                    checked={field.value ?? false}
                                    onChange={e => field.onChange?.(e.currentTarget.checked)}
                                />
                            )}
                        />
                    </Group>
                    <Group label="图标">
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>暂停时图标</Text>
                            <Controller
                                control={control}
                                name="pausedIcon"
                                render={({ field }) => {
                                    const value = audioIconPickerValue(field.value, legacyIcon)
                                    return (
                                        <Tooltip disabled={disabledWithVersion} title={value?.value ? '更换图标' : '添加图标'}>
                                            <IconPicker
                                                clearable
                                                disabled={disabledWithVersion}
                                                value={value}
                                                uploadParams={uploadManagerInAppParams()}
                                                onGetFullUrl={url => getImageFullUrlInApplication(appId, url)}
                                                onChange={field.onChange}
                                                onClear={() => field.onChange(EMPTY_AUDIO_ICON)}
                                            />
                                        </Tooltip>
                                    )
                                }}
                            />
                        </ListItem4ByecodeUi>
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>播放时图标</Text>
                            <Controller
                                control={control}
                                name="playingIcon"
                                render={({ field }) => {
                                    const value = audioIconPickerValue(field.value, legacyIcon)
                                    return (
                                        <Tooltip disabled={disabledWithVersion} title={value?.value ? '更换图标' : '添加图标'}>
                                            <IconPicker
                                                clearable
                                                disabled={disabledWithVersion}
                                                value={value}
                                                uploadParams={uploadManagerInAppParams()}
                                                onGetFullUrl={url => getImageFullUrlInApplication(appId, url)}
                                                onChange={field.onChange}
                                                onClear={() => field.onChange(EMPTY_AUDIO_ICON)}
                                            />
                                        </Tooltip>
                                    )
                                }}
                            />
                        </ListItem4ByecodeUi>
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>默认图标颜色</Text>
                            <Controller
                                control={control}
                                name="iconColor"
                                render={({ field }) => (
                                    <FillColor
                                        label="默认图标颜色"
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                    />
                                )}
                            />
                        </ListItem4ByecodeUi>
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>播放时图标颜色</Text>
                            <Controller
                                control={control}
                                name="playingIconColor"
                                render={({ field }) => (
                                    <FillColor
                                        label="播放时图标颜色"
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                    />
                                )}
                            />
                        </ListItem4ByecodeUi>
                    </Group>
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && (
                <>
                    <Controller
                        name="eventList"
                        control={control}
                        render={({ field }) => <EventList blockType="audio" value={field.value || []} onChange={field.onChange} />}
                    />
                    <Divider />
                    <AnimationSetting />
                </>
            )}
        </>
    )
}
